import { doc, getDoc, getDocFromServer, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '@/lib/firebase';

const getDb = () => db;

export type RingTheme = {
  id: string;
  name: string;
  description?: string;
  colors: string[];
  ringWidth?: number;
  ringGap?: number;
  visibleRingCount?: number;
  ringOrder?: string[];
};

export type RingThemeState = {
  themes: RingTheme[];
  activeThemeId?: string;
  cycleDisplayMode?: 'single' | 'multi';
};

const LOCAL_RING_THEME_PREFIX = 'ring-themes:';
const RING_THEME_COLLECTION = 'ringThemes';

const shouldDisableFirestore = () =>
  typeof window !== 'undefined' && window.localStorage?.getItem('disableFirestore') === '1';

const getStorageKey = (viewId: string) => `${LOCAL_RING_THEME_PREFIX}${viewId}`;
const getDocId = (viewId: string) => `ring-themes-${viewId}`;
const getDocRef = (viewId: string) => {
  const db = getDb();
  if (!db) throw new Error('Firestore is not configured.');
  return doc(db, RING_THEME_COLLECTION, getDocId(viewId));
};

const logFirestoreWrite = (detail: Record<string, unknown>) => {
  try {
    console.info('[firestore:write] ringThemeStore', detail);
  } catch {
    // ignore
  }
};

const sanitizeState = (state: RingThemeState): RingThemeState => ({
  themes: (state.themes ?? []).map((theme) => ({
    id: theme.id,
    name: theme.name,
    description: theme.description,
    colors: theme.colors,
    ringWidth: theme.ringWidth,
    ringGap: theme.ringGap,
    visibleRingCount: theme.visibleRingCount,
    ringOrder: theme.ringOrder,
  })),
  activeThemeId: state.activeThemeId,
  cycleDisplayMode: state.cycleDisplayMode === 'single' ? 'single' : 'multi',
});

export function loadRingThemeState(viewId: string): RingThemeState {
  if (!viewId) {
    return { themes: [] };
  }
  const stored = localStorage.getItem(getStorageKey(viewId));
  if (!stored) {
    return { themes: [] };
  }
  try {
    const parsed = JSON.parse(stored) as RingThemeState;
    return {
      themes: parsed.themes ?? [],
      activeThemeId: parsed.activeThemeId,
      cycleDisplayMode: parsed.cycleDisplayMode === 'single' ? 'single' : 'multi',
    };
  } catch {
    return { themes: [] };
  }
}

export const saveRingThemeState = (viewId: string, state: RingThemeState): void => {
  if (!viewId) return;
  const sanitized = sanitizeState(state);
  try {
    localStorage.setItem(getStorageKey(viewId), JSON.stringify(sanitized));
  } catch {
    // ignore local errors
  }

  const db = getDb();
  if (!db || shouldDisableFirestore()) return;
  try {
    logFirestoreWrite({ viewId, action: 'saveThemeState', themes: sanitized.themes.length });
    void setDoc(getDocRef(viewId), sanitized, { merge: true });
  } catch {
    // ignore network errors
  }
};

const loadRemoteRingThemeState = async (viewId: string): Promise<RingThemeState | null> => {
  const db = getDb();
  if (!db || shouldDisableFirestore()) return null;
  const ref = getDocRef(viewId);
  try {
    const snapshot = await getDocFromServer(ref);
    if (snapshot.exists()) return sanitizeState(snapshot.data() as RingThemeState);
  } catch {
    const snapshot = await getDoc(ref);
    if (snapshot.exists()) return sanitizeState(snapshot.data() as RingThemeState);
  }
  return null;
};

export const subscribeRingThemeState = (
  viewId: string,
  onData: (state: RingThemeState) => void,
  onError?: (error: Error) => void
): (() => void) | null => {
  onData(loadRingThemeState(viewId));

  const db = getDb();
  if (!db || shouldDisableFirestore()) return null;

  const ref = getDocRef(viewId);
  const unsubscribe = onSnapshot(
    ref,
    (snapshot) => {
      if (!snapshot.exists()) return;
      const next = sanitizeState(snapshot.data() as RingThemeState);
      try {
        localStorage.setItem(getStorageKey(viewId), JSON.stringify(next));
      } catch {
        // ignore local errors
      }
      onData(next);
    },
    (error) => {
      onError?.(error as Error);
    }
  );

  void loadRemoteRingThemeState(viewId)
    .then((remote) => {
      if (remote) {
        try {
          localStorage.setItem(getStorageKey(viewId), JSON.stringify(remote));
        } catch {
          // ignore local errors
        }
        onData(remote);
      }
    })
    .catch(() => undefined);

  return unsubscribe;
};
